import * as Api from '@ViewModels';
import { css, StyleDeclarationValue } from 'aphrodite';
import { observer } from 'mobx-react';
import * as React from 'react';
import { Noop } from '../../../../extViewmodels/Utils';
import { IPeopleSearchAddedTagsState, PersistentStoragePeopleSearchAddedTagsStateKey } from '../../../../models';
import { useEventLogging } from '../../../../models/Logging';
import { PersistentStorageManager } from '../../../../models/Storage';
import { useContactSuggestedTagsQuery } from '../../../../queries';
import { AutoCompleteSearchField } from '../../autocomplete/AutoCompleteSearchField';
import { ContactsGutterTag } from '../../contacts/ContactsGutterTag';
import { useTagsStorage } from '../../entities/tags/TagsEditor/hooks';
import { TagsList } from '../../entities/tags/TagsEditor/presentation';
import { HistoryIcon } from '../../svgs/icons/HistoryIcon';
import { SearchIcon } from '../../svgs/icons/SearchIcon';
import { green4, navigation } from '../../../styles/colors';
import { PeopleCollapsibleSection } from '../../../containers/PeopleV2/PeopleContainer/PeopleCollapsibleSection';
import { styleSheet } from './styles';

interface IProps {
	className?: string;
	styles?: StyleDeclarationValue[];
	onTagAdded(tag: string): void;
	onTagRemoved(tag: string): void;
	tags: string[];
	disabled?: boolean;
	impersonationContext?: Api.IImpersonationContext;
}

interface IGutterTag {
	isActive: boolean;
	onRemoveButtonClicked(): void;
	showRemoveButton: boolean;
	tag: string;
	updateSelectedTagList(): void;
}

export const PopularTagsGeneric: React.FC<IProps> = observer(
	({ className, styles, onTagAdded, onTagRemoved, tags, disabled = false, impersonationContext }) => {
		const { logEvent } = useEventLogging();
		const [userAddedTags, setUserAddedTags] = React.useState<string[]>([]);
		const [isOpen, setIsOpen] = React.useState(false);
		const { recentSearches, updateTagsRecentSearches } = useTagsStorage();
		const [inputValue, setInputValue] = React.useState('');

		const tagsQuery = useContactSuggestedTagsQuery({
			addKeepInTouchTag: false,
			pageSize: 25,
			impersonationContext,
		});
		const suggestedTags = React.useMemo(() => {
			return (tagsQuery.data as Api.IAccountTag[]) || [];
		}, [tagsQuery.data]);

		React.useEffect(() => {
			loadStoredTags();
			// eslint-disable-next-line react-hooks/exhaustive-deps
		}, []);

		React.useEffect(() => {
			updateStoredTags();
			// eslint-disable-next-line react-hooks/exhaustive-deps
		}, [userAddedTags]);

		const isTagSelected = (tagToCheck: string) => {
			return tags.includes(tagToCheck);
		};

		const userAdded = () => {
			return Array.from(new Set([...userAddedTags, ...tags].filter(x => !suggestedTags.map(y => y.name).includes(x))));
		};

		const toggleTagOnSelectedTagList = (tag: string) => () => {
			if (disabled) return;
			updateTagsRecentSearches(tag);

			const alreadySelected = isTagSelected(tag);

			if (alreadySelected) {
				onTagRemoved(tag);
			} else {
				onTagAdded(tag);
			}

			setInputValue('');
		};

		const onAutocompleteTagSelected = (tag: string) => {
			if (disabled) return;
			if ((suggestedTags.includes(tag) || userAddedTags.includes(tag)) && !tags.includes(tag)) {
				toggleTagOnSelectedTagList(tag)();
			} else if (!userAddedTags.includes(tag) && !suggestedTags.includes(tag)) {
				setUserAddedTags([...userAddedTags, tag]);
				toggleTagOnSelectedTagList(tag)();
			}
			logEvent('AddTagToGutter', { tag: tag.length });
		};

		const removeTagFromUserAddedTagList = (tag: string) => () => {
			if (disabled) return;
			setUserAddedTags(userAddedTags.filter(x => x !== tag));

			if (tags.includes(tag)) {
				toggleTagOnSelectedTagList(tag)();
			}
		};

		const updateStoredTags = () => {
			PersistentStorageManager.local.setObject<IPeopleSearchAddedTagsState>(
				PersistentStoragePeopleSearchAddedTagsStateKey,
				{
					tags: Array.from(new Set([...userAddedTags])),
				}
			);
		};

		const loadStoredTags = () => {
			PersistentStorageManager.local
				.getObject<IPeopleSearchAddedTagsState>(PersistentStoragePeopleSearchAddedTagsStateKey)
				.then(storedPeopleTagsState => {
					const userStoredTags = storedPeopleTagsState?.tags ?? [];
					if (userStoredTags.length === 0) {
						PersistentStorageManager.local.remove(PersistentStoragePeopleSearchAddedTagsStateKey);
					}
					const userAddedTagsLocal = Array.from(new Set([...userAddedTags, ...userStoredTags]));
					setUserAddedTags(userAddedTagsLocal);
				});
		};

		const createGutterTag = (tag: string, isUserAdded: boolean) => {
			const isActive: boolean = isTagSelected(tag);
			return {
				isActive,
				onRemoveButtonClicked: removeTagFromUserAddedTagList(tag),
				showRemoveButton: isUserAdded && !disabled,
				tag,
				updateSelectedTagList: toggleTagOnSelectedTagList(tag),
			};
		};

		const renderTags: IGutterTag[] = [
			...(suggestedTags?.map((tag: Api.IAccountTag) => {
				return createGutterTag(tag.name, false);
			}) || []),
			...(userAdded().map(tag => createGutterTag(tag, true)) || []),
		];

		const handleValueChange = (e: React.ChangeEvent<HTMLInputElement>) => {
			setInputValue(e.target.value);
		};

		const emptyResults =
			recentSearches?.length > 0 ? (
				<>
					{!inputValue ? (
						<TagsList
							title='Recent Searches'
							icon={<HistoryIcon fillColor={green4} className={css(styleSheet.sectionIcon)} />}
							altKey='searches'
							list={recentSearches.slice(0, 6)}
							addTag={tag => onAutocompleteTagSelected(tag?.name)}
						/>
					) : null}
					{inputValue ? (
						<p className={css(styleSheet.noMargin)} key={`${inputValue}-pending`} title={inputValue}>
							<span className={css(styleSheet.createTag)}>{`No Results found for "${inputValue}"`}</span>
						</p>
					) : null}
				</>
			) : undefined;

		return (
			<div className={`${css(styleSheet.container, ...(styles || []))} ${className || ''}`}>
				<PeopleCollapsibleSection
					showLoader={tagsQuery.isPending}
					items={renderTags.map(tag => {
						return (
							<ContactsGutterTag
								className={css(styleSheet.tag)}
								key={tag.tag}
								isActive={tag.isActive}
								onRemoveButtonClicked={removeTagFromUserAddedTagList(tag.tag)}
								showRemoveButton={tag.showRemoveButton}
								tag={tag.tag}
								updateSelectedTagList={disabled ? Noop : toggleTagOnSelectedTagList(tag.tag)}
							/>
						);
					})}
					itemHeightPx={30}
				/>
				{!disabled && (
					<div>
						<AutoCompleteSearchField
							sectionTitleElement={
								<>
									<div className={css(styleSheet.sectionWrap)}>
										<p className={css(styleSheet.sectionTitle)}>Top Results</p>
									</div>
								</>
							}
							emptyResults={emptyResults}
							anchorClassName={css(styleSheet.searchBox)}
							clearSearchFieldAfterSelectingItem={true}
							dropdownContentClassName={css(styleSheet.autocompleteBox)}
							inputId='contactsGutterTagSearchInput'
							inputProps={{
								placeholder: 'Search tags',
								onChange: handleValueChange,
								onFocus: () => setIsOpen(true),
								onBlur: () => setIsOpen(false),
							}}
							leftAccessory={
								<span className={css(styleSheet.searchIcon)}>
									<SearchIcon fillColor={navigation} />
								</span>
							}
							onItemSelected={onAutocompleteTagSelected}
							resultsLimit={25}
							pageSize={25}
							type={Api.ResourceAutoCompleteViewModelType.Tag}
							openOnFocus={Boolean(emptyResults)}
							isOpen={Boolean(emptyResults) || isOpen}
						/>
					</div>
				)}
			</div>
		);
	}
);
